import { useRef } from 'react';
import _isEqual from 'lodash/isEqual';

// Apollo's InMemoryCache can't normalize array fields whose elements have no
// `id` (e.g. `User.finalAccesses`, `Company.plan.accesses`) — it replaces the
// whole array on every write to the parent entity, so callers get a new
// reference on every fetch even when the content is byte-identical. Anything
// that keys a useCallback/useMemo/useEffect on such a value re-runs on every
// unrelated refetch. This returns the same reference across renders as long
// as the content stays deep-equal — a render-phase ref cache (not the
// useState-based "update state during render" pattern), so it settles within
// the same commit rather than triggering an extra render. Runs `_isEqual` on
// every render of every consumer, so keep it off hot render paths with large
// values.
export default function useDeepStableValue<T>(value: T): T {
  const ref = useRef(value);

  if (!_isEqual(ref.current, value)) {
    ref.current = value;
  }

  return ref.current;
}
